import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 no-underline">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = getArticlesByCategory(slug || '');

  const pageTitle = `${category.name} - 91大事件重大${category.name}事件梳理`;
  const pageDesc = `${category.description}。91大事件为你精选${category.name}领域的重大历史事件，提供深度解析与完整事件脉络。`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 91大事件`,
    description: pageDesc,
    url: `https://www.91dashijian.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://www.91dashijian.com/article/${article.id}`,
        },
      })),
    },
  };

  const iconBgMap: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-600',
    accent: 'bg-accent-100 text-accent-600',
    secondary: 'bg-secondary-100 text-secondary-600',
  };

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-500',
    accent: 'bg-accent-500',
    secondary: 'bg-secondary-500',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={`91大事件,${category.name},${category.name}大事件,历史事件${category.name}`}
        canonicalUrl={`https://www.91dashijian.com/category/${slug}`}
      />
      <Navbar />
      <main className="pt-16">
        <section className={`py-10 md:py-14 ${colorMap[category.color] || 'bg-primary-500'}`}>
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 md:w-16 md:h-16 rounded-lg flex items-center justify-center ${iconBgMap[category.color] || iconBgMap.primary}`}>
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-background-50 mb-1">
                  {category.name}
                </h1>
                <p className="text-sm text-background-50/80 max-w-xl">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors no-underline ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    title={article.title}
                    summary={article.summary}
                    category={article.category}
                    categoryName={category.name}
                    date={article.date}
                    readTime={article.readTime}
                    imageUrl={article.imageUrl}
                    tags={article.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}